import React, { useCallback, useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { Link, useNavigate } from "react-router-dom";
import { debounce } from "lodash";
import {
  DocumentTextIcon,
  PlusIcon,
  PencilSquareIcon,
} from "@heroicons/react/24/outline";
import { fetchTextDocuments } from "../../../api/textDocuments";
import NewTextDocumentDialog from "../../../components/dashboard/text-documents/NewTextDocumentDialog";
import SubjectLinkFilter, {
  buildSubjectFilterParams,
} from "../../../components/dashboard/shared/SubjectLinkFilter";

function statusBadge(status) {
  return status === "final"
    ? "bg-emerald-100 text-emerald-800"
    : "bg-amber-100 text-amber-800";
}

function formatDate(value) {
  if (!value) return "-";
  try {
    return new Date(value).toLocaleDateString();
  } catch {
    return value;
  }
}

const DEFAULT_FILTERS = {
  query: "",
  subjectType: "",
  subjectId: null,
  subjectLabel: "",
  status: "",
};

export const TextDocumentsList = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [state, setState] = useState({ data: [], isLoading: true });
  const [filters, setFilters] = useState(DEFAULT_FILTERS);
  const [createOpen, setCreateOpen] = useState(false);

  const load = useCallback(async (nextFilters) => {
    setState((prev) => ({ ...prev, isLoading: true }));
    const res = await fetchTextDocuments({
      ...buildSubjectFilterParams(nextFilters),
      per_page: 100,
    });
    setState({ data: res?.data || [], isLoading: false });
  }, []);

  const debouncedLoad = useMemo(() => debounce((f) => load(f), 250), [load]);

  useEffect(() => {
    debouncedLoad(filters);
    return () => debouncedLoad.cancel();
  }, [filters, debouncedLoad]);

  return (
    <div className="min-h-screen bg-slate-50">
      <div className="mx-auto max-w-7xl px-4 py-8 sm:px-6 lg:px-8">
        <div className="mb-8 rounded-[2rem] border border-slate-200 bg-white px-6 py-6 shadow-sm">
          <div className="flex flex-col gap-6 lg:flex-row lg:items-end lg:justify-between">
            <div>
              <div className="mb-3 inline-flex items-center gap-2 rounded-full bg-slate-100 px-3 py-1 text-xs font-semibold uppercase tracking-[0.2em] text-slate-600">
                <DocumentTextIcon className="h-4 w-4" />
                {t("textDocuments.title")}
              </div>
              <h1 className="text-3xl font-semibold tracking-tight text-slate-950">
                {t("textDocuments.title")}
              </h1>
              <p className="mt-2 max-w-2xl text-sm text-slate-600">
                {t("textDocuments.hero_subtitle")}
              </p>
            </div>
            <button
              type="button"
              onClick={() => setCreateOpen(true)}
              className="inline-flex items-center justify-center gap-2 rounded-full bg-slate-900 px-4 py-2 text-sm font-semibold text-white shadow-sm transition hover:bg-slate-800"
            >
              <PlusIcon className="h-4 w-4" />
              {t("textDocuments.new")}
            </button>
          </div>
        </div>

        <div className="overflow-hidden rounded-[2rem] border border-slate-200 bg-white shadow-sm">
          <SubjectLinkFilter
            filters={filters}
            onChange={setFilters}
            showStatusFilter
            searchPlaceholder={t("textDocuments.search_placeholder")}
          />
          <div className="divide-y divide-slate-100">
            {state.isLoading ? (
              <div className="px-6 py-10 text-sm text-slate-500">{t("Loading...")}</div>
            ) : state.data.length === 0 ? (
              <div className="px-6 py-10 text-sm text-slate-500">
                {t("textDocuments.empty")}
              </div>
            ) : (
              state.data.map((doc) => (
                <Link
                  key={doc.id}
                  to={`/dashboard/text-documents/${doc.id}`}
                  className="flex items-center justify-between px-6 py-4 transition hover:bg-slate-50"
                >
                  <div className="min-w-0">
                    <h3 className="truncate text-sm font-semibold text-slate-950">
                      {doc.title}
                    </h3>
                    <p className="mt-1 text-xs text-slate-500">
                      {doc.subject_label || t("textDocuments.standalone")}
                      <span className="mx-2">·</span>
                      {formatDate(doc.updated_at)}
                      {doc.last_sent_at ? (
                        <>
                          <span className="mx-2">·</span>
                          <span className="text-emerald-700">
                            {t("documentationSend.sent_short")}
                          </span>
                        </>
                      ) : null}
                    </p>
                  </div>
                  <div className="flex items-center gap-3">
                    <span
                      className={`inline-flex rounded-full px-2 py-1 text-xs font-semibold ${statusBadge(
                        doc.status
                      )}`}
                    >
                      {t(`textDocuments.status.${doc.status}`)}
                    </span>
                    <PencilSquareIcon className="h-4 w-4 text-slate-400" />
                  </div>
                </Link>
              ))
            )}
          </div>
        </div>
      </div>

      <NewTextDocumentDialog
        open={createOpen}
        onClose={() => setCreateOpen(false)}
        onCreated={(doc) => {
          setCreateOpen(false);
          if (doc?.id) navigate(`/dashboard/text-documents/${doc.id}`);
        }}
      />
    </div>
  );
};

export default TextDocumentsList;
